import React from 'react';

import { Pin, PIN_ATTR, PIN_DEFAULT } from 'common/constants';
import { convertPxToVw, int } from 'common/utils/helpers';

type BBox = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;

type ChildrenData = {
	[id: string]: {
		id: string;
		pinType: Pin;
		prev?: {
			elementBBoxG: BBox;
			elementBBoxL: BBox;
		};
	};
};

type Props = {
	children: React.ReactNode;
	contentInnerRef: React.RefObject<HTMLDivElement>;
	isEmbedAutoHeight: boolean;
};

const PINS = {
	top: [Pin.lt, Pin.t, Pin.rt, Pin.none] as string[],
	CENTER: [Pin.l, Pin.c, Pin.r] as string[],
	bottom: [Pin.lb, Pin.b, Pin.rb] as string[],
};

export const AUTO_HEIGHT_ATTR = {
	name: 'data-auto-height',
	value: {
		none: undefined, // default
		preserve: 'preserve', // preserve bbox of some element for do not affect on content height (use prev bbox)
	},
} as const;

const mutationObserverOptions = {
	childList: false,
	subtree: false,
	attributes: true,
	// only observe important layout-affecting attributes
	attributeFilter: ['style', 'class', PIN_ATTR, AUTO_HEIGHT_ATTR.name, 'data-path', 'id'],
};

/**
 * @description React component that dynamically calculates size and position of Content BB first level children,
 * to determine, which height have to be assigned to Content BB, to fit all these children. In other words,
 * Content BB height depends on 1st level children size and position.
 *
 * @link https://storycards.atlassian.net/l/cp/VnZi1Ra2
 */
export default class AutoHeight extends React.Component<Props> {
	private resizeObserver?: ResizeObserver;

	private mutationObserver?: MutationObserver;

	private initInterval?: NodeJS.Timeout;

	childrenData: ChildrenData = {};

	creditSpace = 0;

	componentDidMount() {
		this.initInterval = setInterval(this.init, 100);
	}

	componentWillUnmount() {
		clearInterval(this.initInterval);
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();
	}

	getCreditSpace() {
		if (this.creditSpace) return this.creditSpace;
		const content = this.props.contentInnerRef.current;
		return content?.parentElement ? int(getComputedStyle(content.parentElement).paddingBottom) : 0;
	}

	// Observe both size and attribute changes of content and its children to update the content height
	init = () => {
		const content = this.props.contentInnerRef.current;
		if (!content) return;

		clearInterval(this.initInterval);

		// Create observers
		this.resizeObserver = new ResizeObserver(() => this.resizeContent());
		this.mutationObserver = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				if (mutation.type === 'childList') {
					// Stop observing removed nodes
					mutation.removedNodes.forEach(node => {
						if (node instanceof Element) {
							this.unobserveNode(node);
						}
					});
					// Start observing new nodes
					mutation.addedNodes.forEach(node => {
						if (node instanceof Element) {
							this.observeNode(node, mutationObserverOptions);
						}
					});
				}
				this.resizeContent();
			});
		});

		// Observe content container itself
		this.resizeObserver.observe(content);
		this.mutationObserver.observe(content, { childList: true, subtree: false });

		// Observe initial children
		Array.from(content.children).forEach(child => {
			this.observeNode(child, mutationObserverOptions);
		});
	};

	observeNode = (node: Element, options: MutationObserverInit) => {
		this.resizeObserver?.observe(node);
		this.mutationObserver?.observe(node, options);
	};

	unobserveNode = (node: Element) => {
		this.resizeObserver?.unobserve(node);
		if (node.id) {
			delete this.childrenData[node.id];
		}
	};

	getChildrenData = () => {
		const content = this.props.contentInnerRef.current;
		if (!content) return {};

		const result: ChildrenData = {};

		Array.from(content.children).forEach(child => {
			const { id } = child;
			const pinType = child.getAttribute(PIN_ATTR) ?? PIN_DEFAULT;

			if (!id) return;

			result[id] = {
				id,
				pinType: pinType as Pin,
				prev: this.childrenData?.[id]?.prev,
			};
		});

		this.childrenData = result;
		return result;
	};

	getChildBBox = (element: Element, data: ChildrenData[string]) => {
		const isPreserve = element.getAttribute(AUTO_HEIGHT_ATTR.name) === AUTO_HEIGHT_ATTR.value.preserve;
		const prevValues = data.prev;
		const styles = window.getComputedStyle(element);
		let elementBBoxG: BBox; // global, relative to window
		let elementBBoxL: BBox; // local, relative to parent element

		if (isPreserve && prevValues) {
			// Use prev values instead of current. Used usually to discard resize when some element is being animated.
			elementBBoxG = { ...prevValues.elementBBoxG };
			elementBBoxL = { ...prevValues.elementBBoxL };
		} else {
			elementBBoxG = element.getBoundingClientRect();
			elementBBoxL = {
				top: (parseFloat(styles.top) || 0) + (parseFloat(styles.marginTop) || 0),
				left: (parseFloat(styles.left) || 0) + (parseFloat(styles.marginLeft) || 0),
				width: parseFloat(styles.width) || 0,
				height: parseFloat(styles.height) || 0,
			};
		}

		const marginBottom = parseFloat(styles.marginBottom) || 0;

		return { elementBBoxG, elementBBoxL, alignSelf: styles['align-self'], marginBottom };
	};

	getContentHeight = () => {
		const contentInnerRef = this.props.contentInnerRef.current;
		const childrenData = this.getChildrenData();
		const sizeList = [0];

		[...(contentInnerRef?.children ?? [])].forEach(child => {
			if (!childrenData[child.id] || child.clientHeight === 0) {
				return;
			}

			const { pinType } = childrenData[child.id];
			const { elementBBoxG, elementBBoxL, alignSelf, marginBottom } = this.getChildBBox(
				child,
				childrenData[child.id]
			);

			childrenData[child.id].prev = { elementBBoxG, elementBBoxL };

			// skip auto-stretched items (stretched vertically):
			if (alignSelf === 'stretch') {
				return;
			}

			if (PINS.top.includes(pinType)) {
				sizeList.push(elementBBoxL.top + elementBBoxL.height + marginBottom);
			} else if (PINS.CENTER.includes(pinType)) {
				sizeList.push((Math.abs(elementBBoxL.top) + elementBBoxL.height / 2) * 2 + marginBottom);
			} else if (PINS.bottom.includes(pinType)) {
				sizeList.push(elementBBoxL.top * -1 + elementBBoxL.height + marginBottom);
			}
		});

		return Math.max(...sizeList);
	};

	resizeContent = () => {
		const { isEmbedAutoHeight } = this.props;
		const contentHeight = !isEmbedAutoHeight
			? this.getContentHeight() || window.innerHeight - this.getCreditSpace()
			: this.getContentHeight();

		if (this.props.contentInnerRef.current) {
			this.props.contentInnerRef.current.style.height = convertPxToVw(contentHeight);
		}
	};

	render() {
		return this.props.children;
	}
}
